/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import Icon from 'icons/Icon';
import { useRuntimeProperty } from 'platform/PlatformHooks';
import React, { useEffect, useRef } from 'react';
import { ItemID, AnyColumn } from 'componentregistry';
import { useColumnsRuntimeData } from 'components/DataGrid/useColumns';
import { Theme } from '@mui/material';

const useStyles = makeStyles((theme: Theme) => ({
  arrow: {
    position: 'absolute',
    zIndex: 999,
    transition: 'left 300ms ease',
    width: 2,
    backgroundColor: theme.palette.primary.main,
  },
  icon: {
    position: 'absolute',
    top: -5,
    left: -7,
    '& > svg': {
      fill: theme.palette.primary.main,
    },
  },
}));

export interface Props {
  height: number;
  top: number;
  columnLefts: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  gridRef: React.MutableRefObject<HTMLDivElement | null>;
  fixedLeftColumns: number;
  fixedRightColumns: number;
  appUid: string;
  pageId: ItemID;
  itemId: ItemID;
}

export const columnReorderPositionAtom = atom({
  path: [
    'ui',
    'grid',
    'columnReorderPosition',
    ':appUid',
    ':pageId',
    ':itemId',
  ],
  defaultValue: { left: -1, show: false, start: 0, columnIndex: -1 },
});

export default function ColumnReorderArrow({
  height,
  top,
  columnLefts,
  columnWidths,
  gridRef,
  fixedLeftColumns,
  fixedRightColumns,
  appUid,
  itemId,
  pageId,
}: Props): React.ReactElement | null {
  const [scrollLeft, setLeft] = useRuntimeProperty({
    appUid,
    pageId,
    itemId,
    prop: 'left',
    defaultValue: -1,
  });
  const classes = useStyles();
  const { columnIndex, start, left, show } = useStateXAtomValue(
    columnReorderPositionAtom,
    { params: { appUid, pageId, itemId } },
  );
  const { reorderColumns } = useColumnsRuntimeData(appUid, pageId, itemId);
  const gleft = gridRef.current?.getBoundingClientRect().left ?? 0;
  const x = start - gleft + left;
  let l = 0;
  let index = fixedLeftColumns;
  let fromIndex = fixedLeftColumns;
  let toIndex = columnLefts.length - fixedRightColumns;
  let scrollDiff = scrollLeft;
  if (columnIndex < fixedLeftColumns) {
    toIndex = fixedLeftColumns;
    fromIndex = 0;
    index = 0;
    scrollDiff = 0;
  } else if (columnIndex >= columnLefts.length - fixedRightColumns) {
    toIndex = columnLefts.length;
    fromIndex = columnLefts.length - fixedRightColumns;
    index = columnLefts.length - fixedRightColumns;
  }
  for (let i = fromIndex; i < toIndex; i++) {
    if (x < columnLefts[i] - scrollDiff) {
      l = columnLefts[i] - scrollDiff;
      index = i;
      break;
    }
    if (
      columnLefts[i] - scrollDiff <= x &&
      (columnLefts[i] + columnWidths[i] - scrollDiff > x || i === toIndex - 1)
    ) {
      if (x > columnLefts[i] - scrollDiff + columnWidths[i] / 2) {
        l = columnLefts[i] + columnWidths[i] - scrollDiff;
        index = i + 1;
      } else {
        l = columnLefts[i] - scrollDiff;
        index = i;
      }
      break;
    }
  }
  // console.log({
  //   x,
  //   l,
  //   scrollLeft,
  //   scrollDiff,
  //   start,
  // });
  const ref = useRef({ show, index, columnIndex });
  const get = useStateXGetter();
  const set = useStateXSetter();
  useEffect(() => {
    if (!show && ref.current.show) {
      // console.log(ref.current.index, ref.current.columnIndex);
      reorderColumns(ref.current.columnIndex, ref.current.index);
    }
    ref.current = { show, index, columnIndex };
  }, [columnIndex, get, index, itemId, pageId, reorderColumns, set, show]);

  useEffect(() => {
    if (l < gleft || x < 0) {
      setLeft(0);
    }
  }, [gleft, l, setLeft, x]);

  if (!show) return null;

  return (
    <div className={classes.arrow} style={{ height, top, left: l }}>
      <Icon iconType="regular" className={classes.icon} iconName="angle-up" />
    </div>
  );
}
